"use client";

/**
 * Provider for the public landing page view
 * Sets up BuilderContext and initializes the builder store so existing renderers
 * (NavbarRenderer, HeroRenderer, etc.) work without modification
 */

import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { FetchToastProvider, useToast } from "@/components/providers/FetchToastProvider";
import { BuilderContext, type BuilderContextValue } from "../../_hooks/useBuilderContext";
import { useBuilderStore } from "../../_stores/builder-store";
import { resolveBrandPalette } from "../../_logic/palette";
import type { PageComponent } from "../../_types";
import type { EventLandingPagePublicRow } from "../../_logic/types/db-types";
import type { PublicPageInitialData } from "../../_logic/types/public-page-data";
import type { ViewerIdentity } from "../../_logic/actions/_shared/get-invited-page-access";
import type { ScheduleBannerProps } from "../../_logic/actions/_shared/schedule-banner";
import { PublicActivitiesDataProvider } from "./PublicActivitiesDataProvider";
import { PublicSignupCountsProvider } from "./PublicSignupCountsProvider";
import { PublicViewContext } from "./PublicViewContext";
import { SchedulePublicDataProvider } from "./SchedulePublicDataProvider";

/** Surfaces a toast when the attendee-dashboard resolver redirects back with ?dashboard_error=<code>. */
const DASHBOARD_ERROR_MESSAGES: Record<string, string> = {
  not_an_attendee: "You don't have an attendee record for this event.",
};

function DashboardErrorToast(): null {
  const { showToast } = useToast();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    const code = searchParams.get("dashboard_error");
    if (!code) return;
    firedRef.current = true;

    showToast(DASHBOARD_ERROR_MESSAGES[code] ?? "Something went wrong loading your dashboard.", "error");

    // Strip the param so refresh / share doesn't re-fire the toast.
    const next = new URLSearchParams(searchParams.toString());
    next.delete("dashboard_error");
    const query = next.toString();
    router.replace(query ? `${pathname}?${query}` : pathname, { scroll: false });
  }, [searchParams, pathname, router, showToast]);

  return null;
}

interface PublicPageProviderProps {
  /** The landing page row from the database */
  page: EventLandingPagePublicRow;
  /** Parsed layout_tree components */
  components: PageComponent[];
  /** Event-level IANA timezone (hero, activities, schedule, .ics). NULL = floating time. */
  eventTimeZone: string | null;
  /** Schedule data-fetch + live-banner props, resolved server-side (see `resolveScheduleBannerProps`). */
  scheduleBanner: ScheduleBannerProps;
  /**
   * Top-level (root) page slug. Passed when `page` is a subpage so context consumers can build links
   * to the top-level page (e.g. the focused schedule). Defaults to `page.slug` when omitted (main
   * page and focused-view wrapper, where `page` is already the top-level row).
   */
  rootSlug?: string;
  /** Whether the current user is an organizer (admin/owner) */
  isOrganizer?: boolean;
  /**
   * Server-resolved viewer identity for the logged-in user. Null when the
   * page is public and the visitor isn't signed in.
   */
  viewer?: ViewerIdentity | null;
  /**
   * Opportunistic FK to nowadays.attendees populated when the logged-in
   * viewer's verified email matches the event's roster.
   */
  attendeeId?: number | null;
  /** Whether the viewer may preview `test_publish` activities — gates their visibility. */
  canPreviewTestPublish?: boolean;
  /**
   * Server-fetched initial data for the interactive sections (activities, schedule, signup counts,
   * viewer registrations). Seeds the section providers so they paint without a post-hydration fetch.
   * Null in the builder / when the caller didn't prefetch (each provider then fetches on mount).
   */
  initialData?: PublicPageInitialData | null;
  children: React.ReactNode;
}

/**
 * Wraps the public view with BuilderContext, initializes the builder store
 * with layout_tree components, and provides FetchToastProvider
 *
 * @param props - See PublicPageProviderProps.
 * @returns The provider tree wrapping `children`.
 */
export function PublicPageProvider({
  page,
  components,
  eventTimeZone,
  scheduleBanner,
  rootSlug,
  isOrganizer = false,
  viewer = null,
  attendeeId = null,
  canPreviewTestPublish = false,
  initialData = null,
  children,
}: PublicPageProviderProps): React.ReactElement {
  // Hooks
  const initialize = useBuilderStore((s) => s.initialize);
  const reset = useBuilderStore((s) => s.reset);
  // Seed the viewer's registration sets from the server prefetch so the Activities section shows the
  // correct "registered" affordances on first paint (was previously set after a client fetch).
  const seededViewerRegs = initialData?.viewerRegistrations;
  const [signedUpActivityIds, setSignedUpActivityIds] = useState<Set<number>>(
    () => new Set(seededViewerRegs?.activeActivityIds ?? [])
  );
  const [signedUpSlotIds, setSignedUpSlotIds] = useState<Set<number>>(
    () => new Set(seededViewerRegs?.activeSlotIds ?? [])
  );
  const [waitlistedSlotIds, setWaitlistedSlotIds] = useState<Set<number>>(
    () => new Set(seededViewerRegs?.waitlistedSlotIds ?? [])
  );
  const [registrationBySlotId, setRegistrationBySlotId] = useState<Map<number, number>>(
    () => new Map(seededViewerRegs?.registrationBySlotId ?? [])
  );
  // Seeded from the server read's own outcome so the very first paint can distinguish "you have
  // no registrations" from "we couldn't read them" — the sets above are empty either way.
  const [registrationsUnavailable, setRegistrationsUnavailable] = useState<boolean>(
    () => seededViewerRegs?.lookupFailed ?? false
  );
  // Change signal so sections that fetch their own registration view (ScheduleRenderer) re-sync
  // after a signup/cancel made anywhere on the page. See PublicViewContext.registrationsVersion.
  const [registrationsVersion, setRegistrationsVersion] = useState(0);
  const bumpRegistrations = useCallback(() => setRegistrationsVersion((v) => v + 1), []);

  // Derived values
  const eventId = page.event_id;
  const pageId = page.id;
  const parentPageId = page.parent_page_id ?? page.id;
  const timeZone = eventTimeZone;
  // Prefetch per-slot signup counts only when a surface can open the signup modal: the page has an
  // Activities section, or the family has a Schedule (whose items can link to activities).
  const hasActivitiesSection = components.some((c) => c.type === "activities");
  const prefetchSignupCounts = hasActivitiesSection || scheduleBanner.enableSchedule;

  // Effects — initialize store so NavbarRenderer's useSections() works
  useEffect(() => {
    initialize(pageId, components);
    return () => reset();
  }, [pageId, components, initialize, reset]);

  // Derived values — builder context.
  // `page` is the row actually being rendered (parent or subpage). When it's a subpage,
  // its `parent_page_id` is set, and `page.slug` is the subpage slug — surface that
  // through `currentSubpageSlug` so consumers (e.g. NavbarRenderer's current-page
  // emphasis) can tell which page is active.
  const isSubpage = page.parent_page_id !== null && page.parent_page_id !== undefined;
  const contextValue: BuilderContextValue = {
    eventId,
    parentSlug: page.slug,
    // Always the top-level slug: the subpage route threads it in; the main page / focused wrapper
    // omit it and fall back to `page.slug` (already the top-level row there).
    rootSlug: rootSlug ?? page.slug,
    parentPageId,
    currentSubpageSlug: isSubpage ? page.slug : null,
    ...resolveBrandPalette(page.primary_color, page.brand_palette),
    eventData: null,
    timeZone,
    accessLevel: page.access_level,
  };

  // Derived values — public view context
  const publicViewValue = useMemo(
    () => ({
      isPublic: true as const,
      eventId,
      landingPageId: pageId,
      isOrganizer,
      slug: page.slug,
      activitySignupMode: page.activity_signup_mode,
      viewer,
      attendeeId,
      canPreviewTestPublish,
      timeZone,
      signedUpActivityIds,
      setSignedUpActivityIds,
      signedUpSlotIds,
      setSignedUpSlotIds,
      waitlistedSlotIds,
      setWaitlistedSlotIds,
      registrationBySlotId,
      setRegistrationBySlotId,
      registrationsUnavailable,
      setRegistrationsUnavailable,
      registrationsVersion,
      bumpRegistrations,
      initialViewerRegisteredKeys: seededViewerRegs?.registeredKeys ?? null,
    }),
    [
      eventId,
      pageId,
      isOrganizer,
      page.slug,
      page.activity_signup_mode,
      viewer,
      attendeeId,
      canPreviewTestPublish,
      timeZone,
      signedUpActivityIds,
      setSignedUpActivityIds,
      signedUpSlotIds,
      setSignedUpSlotIds,
      waitlistedSlotIds,
      setWaitlistedSlotIds,
      registrationBySlotId,
      setRegistrationBySlotId,
      registrationsUnavailable,
      setRegistrationsUnavailable,
      registrationsVersion,
      bumpRegistrations,
      seededViewerRegs,
    ]
  );

  // Render
  return (
    <BuilderContext.Provider value={contextValue}>
      <PublicViewContext.Provider value={publicViewValue}>
        <FetchToastProvider>
          <DashboardErrorToast />
          <SchedulePublicDataProvider
            enabled={scheduleBanner.enableSchedule}
            bannerEnabled={scheduleBanner.bannerEnabled}
            scheduleHref={scheduleBanner.scheduleHref}
            initialData={initialData?.schedule ?? null}
          >
            <PublicSignupCountsProvider enabled={prefetchSignupCounts} initialSlotCounts={initialData?.slotCounts}>
              <PublicActivitiesDataProvider initialData={initialData?.activities ?? null}>
                {children}
              </PublicActivitiesDataProvider>
            </PublicSignupCountsProvider>
          </SchedulePublicDataProvider>
        </FetchToastProvider>
      </PublicViewContext.Provider>
    </BuilderContext.Provider>
  );
}
